import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import DeviationImpactAssessmentAccordion from './DeviationImpactAssessmentAccordion';
import { Label, Select, SelectItem } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { CircleIcon } from '../../../components/ui/Icons';

/**
 * DeviationCriticalityAndCauses - Wrapper component that renders Criticality Categorization field
 * followed by the Impact Assessment accordion within the "Assessment of Criticality and Impact" section
 */
const DeviationCriticalityAndCauses = ({ 
  formData,
  setFormData,
  config,
  fields,
  isEditMode = false,
  selectedRecord = null
}) => {
  const { theme } = useTheme();

  // Determine if we're in view mode
  const isViewMode = selectedRecord && !isEditMode;
  const recordData = isViewMode ? selectedRecord : (formData || {});

  // Find the criticalityId field
  const criticalityField = fields?.find(f => f.name === 'criticalityId');
  
  // Fetch criticality options from lookup endpoint
  const lookupEndpoint = criticalityField?.lookupEndpoint 
    ? config?.api?.lookupEndpoints?.[criticalityField.lookupEndpoint]
    : null;
  const { data: criticalityOptions = [], isLoading: criticalityLoading } = useFlexibleList(lookupEndpoint);
  
  const criticalityValue = recordData?.[criticalityField?.name];
  const criticalityDisplay = criticalityValue 
    ? (recordData?.criticality?.name || criticalityOptions.find(opt => opt.id === criticalityValue)?.name || criticalityValue)
    : null;

  const handleCriticalityChange = (value) => {
    if (isViewMode || !setFormData) return; // Don't allow changes in view mode
    setFormData(prev => ({
      ...prev,
      [criticalityField?.name]: value ? parseInt(value) : null
    }));
  };

  // Always render the section content
  return (
    <div className="space-y-6">
      {/* Render Criticality Categorization field first */}
      {criticalityField && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor={criticalityField.name}>
              {criticalityField.label}
            </Label>
            {isViewMode ? (
              <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                {criticalityDisplay ? (
                  <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.primary, theme.text.inverse)}>
                    {String(criticalityDisplay)}
                  </span>
                ) : (
                  <span className={cn("inline-flex items-center gap-1", theme.text.muted)}>
                    <CircleIcon className="w-4 h-4" />
                    <span>—</span>
                  </span>
                )}
              </div>
            ) : (
              <Select
                id={criticalityField.name}
                value={criticalityValue?.toString() || ''}
                onChange={(e) => handleCriticalityChange(e.target.value)}
                disabled={criticalityLoading}
              >
                <SelectItem value="">-- Select --</SelectItem>
                {criticalityOptions.map(option => (
                  <SelectItem key={option.id} value={option.id.toString()}>
                    {option.name || option.label || option.title}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>
        </div>
      )}

      {/* Render Impact Assessment Accordion - Always render, even if data is empty */}
      <DeviationImpactAssessmentAccordion
        formData={recordData || {}}
        setFormData={setFormData || (() => {})}
        config={config}
        fields={fields || []}
        isEditMode={isEditMode}
        selectedRecord={selectedRecord}
      />
    </div>
  );
};

export default DeviationCriticalityAndCauses;

